import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector,
  styled,
  stepConnectorClasses,
  StepIconProps,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckIcon from '@mui/icons-material/Check';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import srcIcon from 'assets/images/Lightbulb.png';
import StyledAccordion from '../../../_shared/accordion';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useIsMobile, useSurveyContent } from '../../../../shared/hooks';
import { surveyPortalMixpanel } from '../../../../vendor/mixpanel';
import { useHandleSubmit, useOnboardingSelectors } from '../../survey/onboarding/Home/hooks';
import { redirectToUrl } from '../../survey/onboarding/Home/helpers';
import titleCase from '../../../../shared/helpers/titleCase';
import { ROUTES } from 'shared/routes/constants';
import { Link } from 'react-router-dom';

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 11,
    padding: 0,
    position: 'relative',
    top: '-18px',
    backgroundColor: 'none',
    border: 'none',
  },
  [`& .${stepConnectorClasses.line}`]: {
    border: 'none',
    backgroundColor: 'none',
    display: 'none',
  },
}));

const CustomStepIcon: React.FC<StepIconProps> = ({ active, completed, className, icon }) => (
  <div
    className={className}
    style={{
      backgroundColor: active
        ? 'rgba(187, 230, 240, 1)'
        : completed
        ? 'gray'
        : 'rgba(38, 43, 45, 0.12)',
      color: active ? 'rgba(38, 43, 45, 1)' : 'rgba(114, 117, 119, 1)',
      borderRadius: '50%',
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 400,
      fontSize: '0.875rem',
    }}
  >
    {icon}
  </div>
);

const OnboardingPage: React.FC = () => {
  const { surveyContent } = useSurveyContent();
  const [accepted, setAccepted] = React.useState(false);
  const [expandedAccordion, setExpandedAccordion] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [, setShowWarning] = React.useState(false);
  const handleSubmit = useHandleSubmit();
  const { redirect_to } = useOnboardingSelectors();
  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccepted(e.target.checked);
  };
  const hasAvailability = surveyContent?.collect_availability;
  const isMobile = useIsMobile();
  const isAIEnabled = surveyContent?.consultation_method === 'ai_moderated';
  const showHints = surveyContent?.content?.hints && surveyContent?.content?.hints.length > 0;
  const hasScreener = surveyContent?.content?.has_screener;
  const screenerDuration = surveyContent?.content?.screener_duration ?? 0;
  const hints = surveyContent?.content?.hints ?? [];
  const eligibility =
    surveyContent?.content?.eligibility_message ??
    'You’ll be a good fit if the following describes you:';
  const userName = surveyContent?.subject_given_name ?? '';
  const professionalType = surveyContent?.content?.professional_type ?? '';
  const surveyObjective = surveyContent?.content?.survey_objective ?? '';
  const surveyCompensation = surveyContent?.content?.survey_compensation ?? '';
  const surveyDuration = surveyContent?.content?.survey_duration ?? '';
  const screenerMethod = surveyContent?.screener_method ?? '';

  const handleStart = () => {
    setLoading(true);
    // Simulate redirect or request delay
    setTimeout(() => {
      const formData = {
        did_accept_terms: true,
        survey_request_url: window.location.href,
      };
      handleSubmit(formData, setShowWarning);
    }, 2000);
  };

  React.useEffect(() => {
    if (redirect_to && surveyContent?.subject) {
      const url = new URL(redirect_to);
      url.searchParams.set('SSEID', surveyContent.subject);
      url.searchParams.set('survey_id', surveyContent.subject);
      if (surveyContent.survey_host) {
        url.searchParams.set('survey_host', surveyContent.survey_host);
      }
      redirectToUrl(url.toString());
    }
  }, [redirect_to, surveyContent]);

  const accordionItems = [
    {
      title: (
        <Box display="flex" marginBottom="8px">
          <Typography
            variant="body1"
            fontWeight="700"
            color="rgba(15, 75, 255, 1)"
            fontSize={'14px'}
          >
            Before You Begin
          </Typography>
          {expandedAccordion.includes('panel0') ? (
            <KeyboardArrowUpIcon
              sx={{
                width: '24px',
                height: '24px',
                marginLeft: '8px',
                color: 'rgba(15, 75, 255, 1)',
              }}
            />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                width: '24px',
                height: '24px',
                marginLeft: '8px',
                color: 'rgba(15, 75, 255, 1)',
              }}
            />
          )}
        </Box>
      ),
      content: (
        <Box>
          <Typography variant="body1" color="text.secondary" mb="12px" style={{ fontSize: '14px' }}>
            {eligibility}
          </Typography>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {hints.map((hint, index) => (
              <li
                key={index}
                style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}
              >
                <CheckIcon
                  sx={{
                    color: 'rgba(42, 158, 86, 1)',
                    width: '18px',
                    height: '18px',
                    marginRight: '6px',
                  }}
                />
                <Typography variant="body1" color="text.secondary" style={{ fontSize: '14px' }}>
                  {hint}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      ),
    },
  ];

  const steps = [
    ...(hasScreener
      ? [
          {
            label: `Initial${screenerMethod === 'ai_interview' ? ' AI' : ''} Screener`,
            subtitle: `Complete a ${screenerDuration} minutes${
              screenerMethod === 'ai_interview' ? ' AI interview' : ' questionnaire'
            } to determine if this opportunity is a strong match for your expertise.`,
          },
        ]
      : []),
    ...(hasAvailability
      ? [
          {
            label: 'Share Availability',
            subtitle: 'Provide availability for a potential consultation call.',
          },
        ]
      : []),
    {
      label: isAIEnabled ? 'AI Consultation Call' : 'Consultation Call',
      subtitle: isAIEnabled
        ? 'Start your private AI consultation instantly, on your own schedule. Available 24/7 with no appointment needed.'
        : 'Join a private consultation call, scheduled around your availability.',
    },
    {
      label: 'Get Paid',
      subtitle: 'Get compensated for your time, insights, and contribution.',
    },
  ];

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            px: 2,
          }}
        >
          <CircularProgress sx={{ color: '#005BFF', mb: 4 }} />

          <Typography variant="h4" fontWeight="600" mb={3} textAlign="center">
            Preparing your eligibility screener
          </Typography>

          <Box
            sx={{
              backgroundColor: '#E9F6FF',
              borderRadius: '16px',
              padding: '20px',
              maxWidth: '360px',
              width: '100%',
              textAlign: 'justify',
            }}
          >
            <Typography variant="subtitle1" fontWeight="400" fontSize={'16px'} gutterBottom>
              What to Expect:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '1rem' }}>
              <li>
                <Typography variant="body2" component="span">
                  A short screener to check if you qualify for this consultation
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  <strong>Please note:</strong> Completing the screener does not guarantee
                  participation or payment.
                </Typography>
              </li>
            </ul>
          </Box>
        </Box>
      )}
      {!loading && (
        <Box maxWidth="480px" mx="auto" px={2} py={1}>
          <Typography variant="h2" mb={2}>
            Let’s get started, {userName.length > 1 ? titleCase(userName) : ''}
          </Typography>
          <Typography variant="body1" mb={3}>
            We're looking for {professionalType} to participate in a consultation about{' '}
            {surveyObjective}.
          </Typography>

          <Stack direction="row" spacing={isMobile ? 4 : '140px'} mb={4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PaidOutlinedIcon height="24px" width="24px" />
              <Stack direction="column" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight="bold">
                  COMPENSATION
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: 'rgba(15, 70, 255, 1)' }}
                >
                  ${surveyCompensation}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessTimeIcon height="24px" width="24px" />
              <Stack direction="column" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight="bold">
                  DURATION
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: 'rgba(15, 70, 255, 1)' }}
                >
                  {surveyDuration} mins
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '16px',
              boxShadow:
                ' 0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12),  0px 1px 2px -2px rgba(0, 0, 0, 0.16)',
            }}
          >
            <Typography variant="h4" fontWeight="bold" mb={1} mt={'8px'}>
              What’s Ahead?
            </Typography>
            {showHints && (
              <StyledAccordion
                showArrow={false}
                items={accordionItems}
                expandedPanelIds={expandedAccordion}
                defaultExpanded={false}
                onPanelChange={(panelId, isExpanded) =>
                  setExpandedAccordion(isExpanded ? [panelId] : [])
                }
              ></StyledAccordion>
            )}
            <Stepper
              orientation="vertical"
              connector={<CustomConnector />}
              sx={{
                '& .MuiStep-root': {
                  paddingBottom: '16px',
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={index} active>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    <Typography variant="body1" fontWeight="bold">
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent
                    sx={{
                      borderLeft: 'none',
                    }}
                  >
                    <Typography variant="body2">{step.subtitle}</Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Box>
            <Box sx={{ mt: '32px', display: 'flex' }}>
              <Checkbox
                data-testid="termsCheckBox"
                checked={accepted}
                onChange={handleCheckbox}
                sx={{ ml: '-6px', mt: '-10px' }}
              />
              <Typography variant="body1" display="inline" sx={{ fontSize: '12px' }}>
                I accept the{' '}
                <Typography
                  variant="body1"
                  component={Link}
                  to={ROUTES.SURVEY.PATHS.TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', fontSize: '12px' }}
                >
                  Terms & Conditions
                </Typography>{' '}
                and{' '}
                <Typography
                  variant="body1"
                  component={Link}
                  to={ROUTES.SURVEY.PATHS.TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', fontSize: '12px' }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              disabled={!accepted}
              onClick={handleStart}
              endIcon={<ArrowForwardIosIcon sx={{ fontSize: '15px !important' }} />}
              sx={{ mt: 1 }}
            >
              START SCREENER
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default OnboardingPage;
